/**
 * Tien o phia giao dien.
 *
 * May chu tra ve tien duoi dang CHUOI so nguyen don vi nho nhat (`amount_minor`),
 * dung theo quy uoc QT-01..QT-04. Giao dien tuyet doi KHONG duoc doi sang `number`:
 * so thuc cua JavaScript mat do chinh xac tu khoang 9 trieu ty don vi tro len, va
 * mot lan lam tron sai o man hinh cung du lam nguoi choi mat long tin vao so du.
 *
 * Vi vay o day: tinh toan bang `bigint`, hien thi bang chuoi.
 */

export interface MoneyDto {
  amount_minor: string;
  currency: string;
  display: string;
}

const EXPONENT: Readonly<Record<string, number>> = {
  USD: 2, EUR: 2, GBP: 2, CNY: 2, THB: 2,
  VND: 0, JPY: 0, KRW: 0,
  USDT: 6,
};

export function exponentOf(currency: string): number {
  return EXPONENT[currency.toUpperCase()] ?? 2;
}

export function toBigInt(value: MoneyDto | string): bigint {
  return BigInt(typeof value === 'string' ? value : value.amount_minor);
}

/**
 * Hien thi so tien co dau phan nhom hang nghin.
 * `display` may chu tra ve khong co dau phan nhom, nen dinh dang lai o day.
 */
export function formatMoney(
  value: MoneyDto | { minor: bigint; currency: string },
  options: { withCurrency?: boolean; sign?: boolean } = {},
): string {
  const minor = 'amount_minor' in value ? BigInt(value.amount_minor) : value.minor;
  const currency = value.currency;
  const exp = exponentOf(currency);

  const negative = minor < 0n;
  const digits = (negative ? -minor : minor).toString().padStart(exp + 1, '0');
  const whole = digits.slice(0, digits.length - exp) || '0';
  const frac = exp > 0 ? digits.slice(digits.length - exp) : '';

  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = frac ? `${grouped}.${frac}` : grouped;

  const prefix = negative ? '-' : options.sign && minor > 0n ? '+' : '';
  return options.withCurrency ? `${prefix}${body} ${currency}` : `${prefix}${body}`;
}

/** Doc so nguoi dung nhap thanh don vi nho nhat. Nem loi khi khong hop le. */
export function parseAmount(input: string, currency: string): bigint {
  const trimmed = input.trim().replace(/,/g, '');
  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    throw new Error('So tien khong hop le');
  }
  const exp = exponentOf(currency);
  const [intPart = '0', fracPart = ''] = trimmed.split('.');
  if (fracPart.length > exp) {
    throw new Error(`${currency} chi nhan toi da ${exp} chu so thap phan`);
  }
  return BigInt(intPart + fracPart.padEnd(exp, '0'));
}

/** Ty le cuoc: 19200 diem co ban -> "1.92". */
export function formatOdds(oddsBp: number | string | bigint): string {
  const bp = BigInt(oddsBp);
  const whole = bp / 10_000n;
  const frac = (bp % 10_000n).toString().padStart(4, '0').replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : `${whole}.0`;
}

/** Tien thang tiem nang cua mot phieu cuoc, tinh bang bigint. */
export function potentialWin(stakeMinor: bigint, totalOddsBp: bigint): bigint {
  return (stakeMinor * totalOddsBp) / 10_000n;
}

export function sumMinor(values: readonly (MoneyDto | string)[]): bigint {
  return values.reduce<bigint>((total, v) => total + toBigInt(v), 0n);
}
